import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Palette, Wrench, Sparkles, ArrowRight, Phone, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { galleryData } from '../data/mockData';
import ScrollAnimation from '../components/ScrollAnimation';

const GalleryPage = () => {
  const { category } = useParams();
  const [selectedImage, setSelectedImage] = useState(null);
  const [activeTab, setActiveTab] = useState(category || 'color');

  const categories = [
    { key: 'color', data: galleryData.color, icon: <Palette className="w-6 h-6" /> },
    { key: 'wood', data: galleryData.wood, icon: <Wrench className="w-6 h-6" /> },
    { key: 'renovations', data: galleryData.renovations, icon: <Sparkles className="w-6 h-6" /> }
  ];

  const openLightbox = (image, category, index) => {
    setSelectedImage({ image, category, index });
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  const navigateLightbox = (direction) => {
    if (!selectedImage) return;
    const categoryData = galleryData[selectedImage.category];
    const newIndex = direction === 'next' 
      ? (selectedImage.index + 1) % categoryData.images.length 
      : (selectedImage.index - 1 + categoryData.images.length) % categoryData.images.length;
    setSelectedImage({
      ...selectedImage,
      image: categoryData.images[newIndex],
      index: newIndex
    });
  };

  // If specific category is requested but doesn't exist
  if (category && !galleryData[category]) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <ScrollAnimation animation="fadeInUp">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-amber-900 mb-4">קטגוריה לא נמצאה</h1>
            <p className="text-gray-600 mb-8">הקטגוריה המבוקשת אינה קיימת</p>
            <Link 
              to="/gallery" 
              className="bg-amber-600 hover:bg-amber-700 text-white px-8 py-3 rounded-lg font-semibold transition-all duration-300 transform hover:scale-105"
            >
              חזור לגלריה הראשית
            </Link>
          </div>
        </ScrollAnimation>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-amber-50 pt-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        
        {/* Header */}
        <ScrollAnimation animation="fadeInUp">
          <div className="text-center mb-16">
            <h1 className="text-5xl md:text-6xl font-bold text-amber-900 mb-8">
              {category ? galleryData[category].title : 'הגלריות שלנו'}
            </h1>
            <div className="w-32 h-1 bg-gradient-to-r from-amber-400 to-amber-600 mx-auto mb-10 rounded-full"></div>
            <p className="text-2xl text-gray-700 max-w-4xl mx-auto leading-relaxed font-light">
              {category ? galleryData[category].description : 'גלו את מגוון העבודות המרהיבות שלנו בכל התחומים'}
            </p>
          </div>
        </ScrollAnimation>

        {/* Category Tabs (only show if not viewing specific category) */}
        {!category && (
          <ScrollAnimation animation="fadeInUp" delay={200}>
            <div className="flex flex-wrap justify-center gap-4 mb-12">
              {categories.map((cat) => (
                <button
                  key={cat.key}
                  onClick={() => setActiveTab(cat.key)}
                  className={`flex items-center space-x-3 space-x-reverse px-8 py-4 rounded-full font-bold text-lg transition-all duration-300 transform hover:scale-105 ${
                    activeTab === cat.key
                      ? 'bg-gradient-to-r from-amber-600 to-amber-700 text-white shadow-xl'
                      : 'bg-white/80 text-amber-900 hover:bg-white shadow-lg'
                  }`}
                >
                  {cat.icon}
                  <span>{cat.data.title}</span>
                </button>
              ))}
            </div>
          </ScrollAnimation>
        )}

        {/* Gallery Content */}
        {category ? (
          // Single category view
          <SingleCategoryGallery 
            data={galleryData[category]} 
            categoryKey={category}
            onImageClick={openLightbox}
          />
        ) : (
          // All categories view with tabs
          <AllCategoriesGallery 
            activeTab={activeTab}
            categories={categories}
            onImageClick={openLightbox}
          />
        )}

        {/* Call to Action */}
        <ScrollAnimation animation="fadeInUp" delay={800}>
          <div className="bg-gradient-to-r from-amber-600 to-amber-700 rounded-2xl p-12 text-center mt-20">
            <h3 className="text-3xl font-bold text-white mb-6">
              מעוניינים בעבודה דומה?
            </h3>
            <p className="text-xl text-amber-100 mb-8 max-w-2xl mx-auto">
              צרו קשר עכשיו לקבלת הצעת מחיר מותאמת אישית ויעוץ מקצועי ללא התחייבות
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a 
                href="https://wa.me/972528943335?text=שלום, ראיתי את הגלריה שלכם ואני מעוניין לקבל הצעת מחיר"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center bg-white text-amber-700 hover:bg-amber-50 font-bold py-3 px-8 rounded-full transition-all duration-300 transform hover:scale-105 shadow-lg"
              >
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                </svg>
                WhatsApp
              </a>
              <Link 
                to="/#contact"
                className="flex items-center bg-transparent border-2 border-white text-white hover:bg-white hover:text-amber-700 font-bold py-3 px-8 rounded-full transition-all duration-300 transform hover:scale-105"
              >
                <span>צרו קשר</span>
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
            </div>
          </div>
        </ScrollAnimation>
      </div>

      {/* Lightbox Modal */}
      {selectedImage && (
        <LightboxModal 
          selectedImage={selectedImage}
          onClose={closeLightbox}
          onNavigate={navigateLightbox}
        />
      )}
    </div>
  );
};

// Single Category Gallery Component
const SingleCategoryGallery = ({ data, categoryKey, onImageClick }) => (
  <div className="space-y-12">
    <ScrollAnimation animation="fadeInUp" delay={400}>
      <div className="text-center mb-12">
        <h2 className="text-3xl font-bold text-amber-900 mb-4">{data.subtitle}</h2>
        <p className="text-lg text-gray-600 max-w-3xl mx-auto">{data.description}</p>
      </div>
    </ScrollAnimation>

    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
      {data.images.map((image, index) => (
        <ScrollAnimation key={index} animation="fadeInUp" delay={200 + (index * 100)}>
          <div 
            className="group relative overflow-hidden rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:scale-105 cursor-pointer"
            onClick={() => onImageClick(image, categoryKey, index)}
          >
            <img 
              src={image.url} 
              alt={image.title}
              className="w-full h-80 object-cover group-hover:scale-110 transition-transform duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <div className="absolute bottom-0 left-0 right-0 p-6 text-white transform translate-y-full group-hover:translate-y-0 transition-transform duration-300">
              <h3 className="text-xl font-bold mb-2">{image.title}</h3>
              <p className="text-sm text-white/90">{image.description}</p>
            </div>
            <div className="absolute top-4 right-4 w-8 h-8 bg-amber-600 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-lg">
              {index + 1}
            </div>
          </div>
        </ScrollAnimation>
      ))}
    </div>
  </div>
);

// All Categories Gallery Component
const AllCategoriesGallery = ({ activeTab, categories, onImageClick }) => {
  const activeCategory = categories.find(cat => cat.key === activeTab);
  
  return (
    <div className="space-y-12">
      <ScrollAnimation animation="fadeInUp" delay={400}>
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-amber-900 mb-4">{activeCategory.data.subtitle}</h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">{activeCategory.data.description}</p>
        </div>
      </ScrollAnimation>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {activeCategory.data.images.map((image, index) => (
          <ScrollAnimation key={`${activeTab}-${index}`} animation="fadeInUp" delay={200 + (index * 100)}>
            <div 
              className="group relative overflow-hidden rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:scale-105 cursor-pointer"
              onClick={() => onImageClick(image, activeTab, index)}
            >
              <img 
                src={image.url} 
                alt={image.title}
                className="w-full h-80 object-cover group-hover:scale-110 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white transform translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                <h3 className="text-xl font-bold mb-2">{image.title}</h3>
                <p className="text-sm text-white/90">{image.description}</p>
              </div>
              <div className="absolute top-4 right-4 w-8 h-8 bg-amber-600 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-lg">
                {index + 1}
              </div>
            </div>
          </ScrollAnimation>
        ))}
      </div>

      <div className="text-center mt-12">
        <Link 
          to={`/gallery/${activeTab}`}
          className="inline-flex items-center bg-amber-600 hover:bg-amber-700 text-white px-8 py-4 rounded-full font-bold text-lg transition-all duration-300 transform hover:scale-105 shadow-xl"
        >
          <span>צפו בכל העבודות ב{activeCategory.data.title}</span>
          <ArrowRight className="w-5 h-5 ml-3" />
        </Link>
      </div>
    </div>
  );
};

// Lightbox Modal Component
const LightboxModal = ({ selectedImage, onClose, onNavigate }) => (
  <div 
    className="fixed inset-0 z-50 bg-gradient-to-br from-black/85 via-black/90 to-black/95 backdrop-blur-sm flex items-center justify-center p-4"
    onClick={onClose}
  >
    <div 
      className="relative max-w-6xl max-h-full"
      onClick={(e) => e.stopPropagation()}
    >
      <button
        onClick={onClose}
        className="absolute -top-12 right-0 z-10 bg-black/50 hover:bg-black/70 text-white hover:text-amber-400 transition-all duration-300 rounded-full p-2 backdrop-blur-sm border border-white/20"
        aria-label="סגור חלון תמונה"
      >
        <X className="w-8 h-8" />
      </button>
      
      <div className="relative">
        <img
          src={selectedImage.image.url}
          alt={selectedImage.image.title}
          className="max-w-full max-h-[80vh] object-contain rounded-lg shadow-2xl"
        />
        
        <button
          onClick={() => onNavigate('prev')}
          className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 bg-black/50 hover:bg-black/70 text-white hover:text-amber-400 transition-all duration-300 rounded-full p-3 backdrop-blur-sm border border-white/20 shadow-lg"
          aria-label="תמונה קודמת"
        >
          <ChevronRight className="w-8 h-8" />
        </button>
        
        <button
          onClick={() => onNavigate('next')}
          className="absolute right-4 top-1/2 transform -translate-y-1/2 z-10 bg-black/50 hover:bg-black/70 text-white hover:text-amber-400 transition-all duration-300 rounded-full p-3 backdrop-blur-sm border border-white/20 shadow-lg"
          aria-label="תמונה הבאה"
        >
          <ChevronLeft className="w-8 h-8" />
        </button>
      </div>
      
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6 rounded-b-lg">
        <h3 className="text-2xl font-bold text-white mb-2">{selectedImage.image.title}</h3>
        <p className="text-amber-200 mb-2">{selectedImage.image.description}</p>
        <div className="text-amber-300 text-sm">
          {selectedImage.index + 1} / {galleryData[selectedImage.category].images.length}
        </div>
      </div>
    </div>
  </div>
);

export default GalleryPage;